/**
 * The widget disappears when a call ends, taking the focused element with it
 * and dropping focus to `<body>` — a WCAG 2.4.3 focus-order failure. This puts
 * focus back where the widget found it, but only if it actually got lost:
 * reclaiming focus the user has since moved elsewhere is the same theft.
 */
import { useEffect, useRef } from 'react';

export const useFocusRestoration = (): void => {
  const focusOriginRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const { activeElement } = document;
    focusOriginRef.current =
      activeElement instanceof HTMLElement && activeElement !== document.body
        ? activeElement
        : null;

    return () => {
      const origin = focusOriginRef.current;

      // Gone itself — e.g. the banner's Accept button, which unmounts with it.
      if (!origin || !origin.isConnected) {
        return;
      }

      // Anywhere but the document body means the user is there on purpose.
      const current = document.activeElement;
      if (current && current !== document.body) {
        return;
      }

      origin.focus();
    };
  }, []);
};
